import React from 'react';
import {Project} from '../../domain/types';
import {formatRelativeDate, formatExactDate} from '../../lib/formatDate';
import {
  ArrowRightIcon,
  FolderIcon,
  ListIcon,
  PencilIcon,
  StarIcon,
  TrashIcon,
} from '../Icons';

export interface ProjectRowActions {
  onOpen: (project: Project) => void;
  onEdit: (project: Project) => void;
  onDelete: (project: Project) => void;
  onToggleFavorite: (project: Project) => void;
}

interface ProjectRowProps extends ProjectRowActions {
  project: Project;
  /** The project every no-project-in-the-URL page is already pointed at. */
  isCurrent: boolean;
  isBusy: boolean;
  /** Why delete is off for this project, or null when it is allowed. */
  deleteBlockedReason: string | null;
}

const iconButton = 'p-1.5 rounded-lg text-gray-500 hover:text-gray-100 hover:bg-gray-800 '
  + 'transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed';

const badge = 'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[11px] font-medium '
  + 'ring-1 ring-inset';

/**
 * One project, and everything you can do to it.
 *
 * The name is the only part of the row that opens the project rather than the row itself — a row
 * that navigates on click while carrying a delete button is a row where every miss costs something.
 */
const ProjectRow: React.FC<ProjectRowProps> = ({
  project,
  isCurrent,
  isBusy,
  deleteBlockedReason,
  onOpen,
  onEdit,
  onDelete,
  onToggleFavorite,
}) => (
  <div
    className={`group grid grid-cols-12 items-center gap-4 px-4 py-3 border-b border-gray-800/70
     last:border-b-0 transition-colors ${
  isCurrent ? 'bg-indigo-500/[0.07]' : 'hover:bg-gray-800/30'
  }`}
  >
    {/* Project */}
    <div className="col-span-12 md:col-span-4 min-w-0">
      <button
        type="button"
        onClick={() => onOpen(project)}
        aria-label={`Open ${project.name}`}
        className="flex w-full items-start gap-3 text-left cursor-pointer"
      >
        <FolderIcon className={`w-4 h-4 mt-0.5 shrink-0 ${isCurrent ? 'text-indigo-400' : 'text-gray-600'}`} />
        <span className="min-w-0">
          <span className="flex flex-wrap items-center gap-1.5 min-w-0">
            <span className="truncate text-sm font-medium text-gray-100" title={project.name}>
              {project.name}
            </span>
            {project.isDefault && (
              <span className={`${badge} bg-gray-800 text-gray-400 ring-gray-700`} title="Where anything without a project lands">
                Default
              </span>
            )}
            {isCurrent && (
              <span className={`${badge} bg-indigo-500/10 text-indigo-300 ring-indigo-500/30`}>
                Current
              </span>
            )}
          </span>
          {project.description && (
            <span className="mt-0.5 block truncate text-xs text-gray-500" title={project.description}>
              {project.description}
            </span>
          )}
        </span>
      </button>
    </div>

    {/* Transcriptions */}
    <div className="col-span-4 md:col-span-2 text-xs tabular-nums text-gray-400">
      {project.totalTranscriptions} transcription{project.totalTranscriptions === 1 ? '' : 's'}
    </div>

    {/* Default word list */}
    <div className="col-span-8 md:col-span-2 min-w-0">
      {project.defaultWordlistName ? (
        <span className="flex items-center gap-1.5 min-w-0 text-xs text-gray-400">
          <ListIcon className="w-3.5 h-3.5 shrink-0 text-gray-600" />
          <span className="truncate" title={project.defaultWordlistName}>
            {project.defaultWordlistName}
          </span>
        </span>
      ) : (
        <span className="text-xs text-gray-600">No default list</span>
      )}
    </div>

    {/* Updated */}
    <div className="col-span-4 md:col-span-2 text-xs text-gray-400" title={formatExactDate(project.updatedAt)}>
      {formatRelativeDate(project.updatedAt)}
    </div>

    {/* Actions */}
    <div className="col-span-8 md:col-span-2 flex items-center justify-end gap-1">
      <button
        type="button"
        onClick={() => onToggleFavorite(project)}
        disabled={isBusy}
        aria-pressed={project.favorite}
        aria-label={project.favorite ? `Unfavourite ${project.name}` : `Favourite ${project.name}`}
        title={project.favorite ? 'Favourited — favourites sort to the top' : 'Favourite this project'}
        className={`${iconButton} ${project.favorite ? 'text-amber-300 hover:text-amber-200' : ''}`}
      >
        <StarIcon className={`w-4 h-4 ${project.favorite ? 'fill-current' : ''}`} />
      </button>

      <button
        type="button"
        onClick={() => onOpen(project)}
        title="Open this project's workspace"
        className="inline-flex items-center gap-1.5 rounded-lg border border-gray-700 px-2.5 py-1.5
         text-xs font-medium text-gray-200 hover:border-gray-600 hover:bg-gray-800
         transition-colors cursor-pointer"
      >
        Open
        <ArrowRightIcon className="w-3.5 h-3.5" />
      </button>

      <button
        type="button"
        onClick={() => onEdit(project)}
        disabled={isBusy}
        aria-label={`Edit ${project.name}`}
        title="Rename or describe this project"
        className={iconButton}
      >
        <PencilIcon className="w-4 h-4" />
      </button>

      {/* A project that cannot go is shown greyed out with the reason rather than hidden, so the
          rule is discoverable at the moment it applies. The tooltip sits on a wrapper because a
          disabled button gets no mouse events of its own in some browsers. */}
      <span title={deleteBlockedReason ?? 'Delete this project'} className="inline-flex">
        <button
          type="button"
          onClick={() => onDelete(project)}
          disabled={isBusy || deleteBlockedReason !== null}
          aria-label={
            deleteBlockedReason
              ? `Delete ${project.name} — ${deleteBlockedReason}`
              : `Delete ${project.name}`
          }
          className={`${iconButton} ${
            deleteBlockedReason
              ? 'opacity-0 group-hover:opacity-40'
              : 'opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-red-300'
          }`}
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </span>
    </div>
  </div>
);

export default ProjectRow;
